import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { InputText } from "@sellar-io/components";
import { useLocation, useNavigate } from "react-router";
import { InputContainer } from "@sellar-io/components";
import { Icon, ProfilePicture, StockIndicator } from "../../components";
import { useMediaQuery } from "usehooks-ts";
import { twMerge } from "tailwind-merge";
import { useProductAvailabilityTierUpdate } from "../../hooks/api/v2/productAvailabilityTier/useProductAvailabilityTierUpdate";
import { useProductAvailabilityTierCreate } from "../../hooks/api/v2/productAvailabilityTier/useProductAvailabilityTierCreate";
import {
  AvailabilityTierGetResponseType,
  ProductAssignmentListResponseType,
  ProductAssignmentProductType,
  ProductAvailabilityTierPatchRequestType,
} from "@sellar-io/api-v2/types";
import * as ROUTES from "../../constants/routes";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { useAvailabilityTier } from "../../hooks/api/v2/availabilityTier/useAvailabilityTier";
import { useSpotlightFilterStore } from "../spotlight/SpotlightFilterStore";
import { useShallow } from "zustand/shallow";
import { abvFilterType } from "../discovery/v2/DiscoveryV2SearchStore";
import { useDebounce } from "use-debounce";
import { SpotlightFilterContainerCard } from "../spotlight/components/SpotlightFilterContainerCard";
import { useProductAssignmentList } from "../../hooks/api/v2/product/useProductAssignmentList";
import {
  AvailabilityTierMap,
  useAvailabilityTierStore,
} from "./availabilityTierStore";
import { InfiniteList } from "../../components/infiniteLoading/InfiniteList";
import { TradeTermsLayout } from "../../components/tradeTermsAssignment/TradeTermsLayout";
import { AvailabilityTierDetail } from "./AvailabilityTierDetail";
import { useAvailabilityTierAssign } from "../../hooks/api/v2/availabilityTier/useAvailabilityTierAssign";
import { useProductAvailabilityTierPatch } from "../../hooks/api/v2/productAvailabilityTier/useProductAvailabilityTierPatch";
import { DietaryDisplayList } from "../../components/dietaryDisplayList/DietaryDisplayList";
import { InactiveBadge } from "../../components/badge/InactiveBadge";
import { buildDietaryList } from "../../components/dietaryDisplayList/buildDietaryList";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";

export const AvailabilityTier = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const availabilityTierId = useParamAsNumber(
    ROUTES.AVAILABILITY_TIER,
    "availabilityTierId",
  );

  const isUnbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const redirectToCustomerZoneId = state?.customerZoneRedirectId ?? null;
  const launchAssignmentDialog = state?.launchAssignmentDialog ?? false;

  const { setAvailabilityMap } = useAvailabilityTierStore(
    useShallow((state) => ({
      setAvailabilityMap: state.setAvailabilityMap,
    })),
  );

  const availabilityTierAssign = useAvailabilityTierAssign(availabilityTierId);

  const { data: availabilityTier } = useAvailabilityTier(availabilityTierId);

  return (
    <TradeTermsLayout
      title={availabilityTier?.name ?? ""}
      onClickLeftIcon={() =>
        navigate(
          redirectToCustomerZoneId
            ? `${ROUTES.CUSTOMER_ZONE.href({ customerZoneId: redirectToCustomerZoneId })}?tab=productCatalogue`
            : ROUTES.AVAILABILITY_TIERS,
        )
      }
      aside={<AvailabilityTierDetail availabilityTierId={availabilityTierId} />}
      type="product catalogue"
      existingConnections={availabilityTier?.SupplierRetailerConnections ?? []}
      existingCustomerZoneIds={availabilityTier?.CustomerZones.map(
        (cz) => cz.id,
      )}
      onAssign={({
        connectionIds,
        customerZoneIds,
      }: {
        connectionIds: number[];
        customerZoneIds: number[];
      }) => {
        return new Promise((resolve) => {
          availabilityTierAssign.mutate(
            {
              connectionIds,
              customerZoneIds,
            },
            {
              onSuccess: () => {
                setAvailabilityMap({});
                resolve({
                  success: true,
                  message: "",
                });
              },
              onError: () => {
                resolve({
                  success: false,
                  message: "Failed to assign Availability, please try again",
                });
              },
            },
          );
        });
      }}
      showAssignment={launchAssignmentDialog}
      assignmentHeader={
        <>
          <span className="font-semibold">Assign product catalogue</span>
          <p className="text-zinc-500">
            {isUnbundlingEnabled
              ? "Assign this product catalogue to your customers. Any edits you make to this price list will affect all customers you’ve assigned it to."
              : "You can choose to assign your product catalogue to your specified customer zones or individual customers. Any edits you make to this product catalogue will affect all customers you've assigned it to."}
          </p>
        </>
      }
      hideCustomerZones={isUnbundlingEnabled}
    >
      <div className="mx-auto flex w-full max-w-[920px] items-center gap-2">
        <AvailabilityTierContent availabilityTier={availabilityTier} />
      </div>
    </TradeTermsLayout>
  );
};

export const AvailabilityTierContent = ({
  availabilityTier,
  isNewTier,
  readOnly,
}: {
  availabilityTier?: AvailabilityTierGetResponseType;
  isNewTier?: boolean;
  readOnly?: boolean;
}) => {
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebounce(search, 500);

  const isMobile = useMediaQuery("(max-width: 768px)");

  const { availabilityMap, setAvailabilityMap, toggleAvailability } =
    useAvailabilityTierStore(
      useShallow((state) => ({
        availabilityMap: state.availabilityMap,
        setAvailabilityMap: state.setAvailabilityMap,
        toggleAvailability: state.toggleAvailability,
      })),
    );

  const {
    selectedContainerTypes,
    selectedAbvTypes,
    selectedDietaryTypes,
    offersSellarCreditOnly,
    activeOnly,
    inactiveOnly,
  } = useSpotlightFilterStore(
    useShallow((state) => ({
      selectedContainerTypes: state.selectedContainerTypes,
      selectedAbvTypes: state.selectedAbvTypes,
      selectedDietaryTypes: state.selectedDietaryTypes,
      offersSellarCreditOnly: state.offersSellarCreditOnly,
      activeOnly: state.activeOnly,
      inactiveOnly: state.inactiveOnly,
    })),
  );

  const productQuery = useMemo(() => {
    return [
      ...(isNewTier || !availabilityTier
        ? []
        : [
            {
              type: "availabilityTierId",
              modifier: "EQUALS",
              value: availabilityTier.id,
            } as const,
          ]),
      ...(offersSellarCreditOnly
        ? [
            {
              type: "offersSellarCreditOnly",
              modifier: "EQUALS",
              value: offersSellarCreditOnly,
            } as const,
          ]
        : []),
      ...(selectedAbvTypes.length
        ? selectedAbvTypes.map((type) => abvFilterType[type].query)
        : []),
      ...(selectedDietaryTypes.length
        ? [
            {
              type: "dietaryType",
              modifier: "IN",
              value: selectedDietaryTypes,
            } as const,
          ]
        : []),
      ...(selectedContainerTypes.length
        ? [
            {
              type: "containerType",
              modifier: "IN",
              value: selectedContainerTypes as Array<
                "keg" | "cask" | "can" | "bottle" | "bag" | "bagInBox"
              >,
            } as const,
          ]
        : []),
      ...(activeOnly
        ? [
            {
              type: "activeOnly",
              modifier: "EQUALS",
              value: true,
            } as const,
          ]
        : []),
      ...(inactiveOnly
        ? [
            {
              type: "inactiveOnly",
              modifier: "EQUALS",
              value: true,
            } as const,
          ]
        : []),
    ];
  }, [
    isNewTier,
    availabilityTier,
    offersSellarCreditOnly,
    selectedAbvTypes,
    selectedDietaryTypes,
    selectedContainerTypes,
    activeOnly,
    inactiveOnly,
  ]);

  const productListQuery = useProductAssignmentList({
    search: debouncedSearch,
    limit: 5,
    query: productQuery,
  });

  const productList = useMemo(() => {
    return (
      productListQuery.data?.pages.flatMap((page) => page.map((p) => p)) ?? []
    );
  }, [productListQuery.data]);

  const productAvailabilityTierUpdate = useProductAvailabilityTierUpdate();

  const updateAvailability = ({
    id,
    active,
    ProductId,
  }: {
    id: number;
    active: boolean;
    ProductId: number;
  }) => {
    productAvailabilityTierUpdate.mutate(
      { id, active },
      {
        onSuccess: () => {
          toggleAvailability(ProductId, id);
        },
      },
    );
  };

  const productAvailabilityTierCreate = useProductAvailabilityTierCreate();

  const createAvailability = ({
    active,
    ProductId,
  }: {
    active: boolean;
    ProductId: number;
  }) => {
    if (!availabilityTier) return;
    productAvailabilityTierCreate.mutate(
      {
        active,
        ProductId,
        AvailabilityTierId: availabilityTier.id,
      },
      {
        onSuccess: (response) => {
          toggleAvailability(ProductId, response.id);
        },
      },
    );
  };

  const productAvailabilityTierPatch = useProductAvailabilityTierPatch();

  const patchAvailability = (
    updates: ProductAvailabilityTierPatchRequestType,
  ) => {
    productAvailabilityTierPatch.mutate(updates, {
      onSuccess: (response) => {
        response.map((p) => toggleAvailability(p.ProductId, p.id));
      },
    });
  };

  const getName = (product: ProductAssignmentProductType) => {
    const nameParts = product.name?.split(" • ");
    return `${nameParts?.[1]}${nameParts?.[2] ? " • " : ""}${nameParts?.[2] ?? ""}`;
  };

  const getPatchParameters = (
    product: ProductAssignmentProductType,
    active: boolean,
  ): ProductAvailabilityTierPatchRequestType[number] => {
    if (!availabilityTier)
      throw new Error(
        "Cannot update product visibility before loading the catalogue",
      );

    const hasProductAvailabilityTier = availabilityMap[product.id];
    if (!!hasProductAvailabilityTier?.productAvailabilityId) {
      return {
        id: hasProductAvailabilityTier.productAvailabilityId,
        value: {
          id: hasProductAvailabilityTier.productAvailabilityId,
          active,
        },
        action: "PUT",
      };
    } else {
      return {
        value: {
          ProductId: product.id,
          AvailabilityTierId: availabilityTier.id,
          active,
        },
        action: "POST",
      };
    }
  };

  const saveAvailability = (
    product: ProductAssignmentProductType,
    active: boolean,
  ) => {
    const hasProductAvailabilityTier = availabilityMap[product.id];
    if (!!hasProductAvailabilityTier?.productAvailabilityId) {
      updateAvailability({
        id: hasProductAvailabilityTier.productAvailabilityId,
        active,
        ProductId: product.id,
      });
    } else {
      createAvailability({
        active,
        ProductId: product.id,
      });
    }
  };

  const syncAvailabilityMap = useEffectEvent(() => {
    const newMap: AvailabilityTierMap = {};
    if (!availabilityTier || isNewTier) {
      setAvailabilityMap(newMap);
      return;
    }

    productList.forEach((parent) => {
      parent.Products.forEach((p) => {
        if (!newMap[p.id]) {
          const productAvailability = p.ProductAvailabilityTiers.find(
            (at) => at.AvailabilityTierId === availabilityTier.id,
          );
          newMap[p.id] = {
            productAvailabilityId: productAvailability?.id,
            active: productAvailability?.active ?? false,
          };
        }
      });
    });
    setAvailabilityMap(newMap);
  });

  useEffect(() => {
    syncAvailabilityMap();
  }, [productListQuery.data]);

  return (
    <div className="mb-8 flex w-full flex-col gap-4 px-4">
      <SpotlightFilterContainerCard
        sticky={false}
        mobileBorder={true}
        includeActiveToggle={true}
      >
        <InputContainer className="w-full flex-none py-[0.4rem] sm:max-w-[280px]">
          <Icon icon="MAGNIFY" />
          <InputText
            required={false}
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />
        </InputContainer>
      </SpotlightFilterContainerCard>
      <InfiniteList
        query={productListQuery}
        className={twMerge(
          "flex flex-col gap-4 pb-40",
          readOnly && "pointer-events-none",
        )}
      >
        {productList.map((parent) => (
          <div
            className={twMerge(
              "flex flex-col rounded-lg border border-zinc-300",
              parent.active ? "bg-white" : "bg-zinc-50",
            )}
            key={parent.id}
          >
            <div className="flex items-center gap-2 p-4">
              <ProfilePicture logoUrl={parent.imageUrl} size="large" />
              <div className="flex flex-col gap-0.5">
                <span className="text-2xl font-semibold">{parent.name}</span>
                <span className="text-zinc-500">
                  {parent.kind}
                  {" // "}
                  {parent.abv}%
                </span>
              </div>
              <div className="-mr-4 ml-auto flex items-center gap-2">
                {!parent.active && <InactiveBadge productId={parent.id} />}
                <span className="flex w-20 justify-center">
                  {!isNewTier && availabilityTier && (
                    <ProductCheckbox
                      parent={parent}
                      availabilityTierId={availabilityTier.id}
                      onBulkToggle={(ids, state) => {
                        if (!availabilityTier.id) return;
                        patchAvailability(
                          parent.Products.filter((p) => ids.includes(p.id)).map(
                            (product) => getPatchParameters(product, state),
                          ),
                        );
                      }}
                    />
                  )}
                </span>
              </div>
            </div>
            <div
              className={twMerge(
                "grid border-t border-zinc-300",
                isMobile
                  ? "grid-cols-[1fr_80px]"
                  : "grid-cols-[repeat(3,_1fr)_80px]",
              )}
            >
              <span className="flex items-center border-b border-zinc-300 bg-zinc-100 px-4 py-1.5 text-sm font-semibold">
                Format
              </span>
              {!isMobile && (
                <>
                  <span className="flex items-center border-b border-zinc-300 bg-zinc-100 px-4 py-1.5 text-sm font-semibold">
                    Dietary
                  </span>
                  <span className="flex items-center border-b border-zinc-300 bg-zinc-100 px-4 py-1.5 text-sm font-semibold">
                    Stock
                  </span>
                </>
              )}
              <span className="flex items-center justify-center border-b border-zinc-300 bg-zinc-100 px-4 py-1.5 text-sm font-semibold">
                <Icon icon="EYE" />
              </span>
              {parent.Products.map((product, idx) => {
                const dietaryList = buildDietaryList({
                  childProduct: product,
                  parentProduct: parent,
                });
                return (
                  <>
                    <span
                      className={twMerge(
                        "flex flex-col justify-center gap-1 border-zinc-300 px-4 py-3 font-semibold",
                        idx !== parent.Products.length - 1 && "border-b",
                      )}
                    >
                      {getName(product)}
                      {isMobile && (
                        <span className="flex items-center gap-2 text-sm font-normal">
                          <StockIndicator
                            stock={product.availableStock}
                            type="minimal"
                          />{" "}
                          <DietaryDisplayList
                            dietaryRequirements={{
                              VG: dietaryList.includes("VG"),
                              LF: dietaryList.includes("LF"),
                              GF: dietaryList.includes("GF"),
                            }}
                            mode="text"
                          />
                        </span>
                      )}
                    </span>
                    {!isMobile && (
                      <>
                        <span
                          className={twMerge(
                            "flex items-center border-zinc-300 px-4 py-3",
                            idx !== parent.Products.length - 1 && "border-b",
                          )}
                        >
                          <DietaryDisplayList
                            dietaryRequirements={{
                              VG: dietaryList.includes("VG"),
                              LF: dietaryList.includes("LF"),
                              GF: dietaryList.includes("GF"),
                            }}
                            mode="text"
                          />
                        </span>
                        <span
                          className={twMerge(
                            "flex items-center border-zinc-300 px-4 py-3",
                            idx !== parent.Products.length - 1 && "border-b",
                          )}
                        >
                          <StockIndicator
                            stock={product.availableStock}
                            type="short"
                            canBackorder={product.canBackorder}
                          />
                        </span>
                      </>
                    )}
                    <span
                      className={twMerge(
                        "flex items-center justify-center border-zinc-300 px-4 py-3",
                        idx !== parent.Products.length - 1 && "border-b",
                      )}
                    >
                      {!isNewTier && (
                        <input
                          type="checkbox"
                          checked={availabilityMap[product.id]?.active ?? false}
                          onChange={(e) =>
                            saveAvailability(product, e.target.checked)
                          }
                        />
                      )}
                    </span>
                  </>
                );
              })}
            </div>
          </div>
        ))}
      </InfiniteList>
    </div>
  );
};

const ProductCheckbox = ({
  parent,
  availabilityTierId,
  onBulkToggle,
}: {
  parent: ProductAssignmentListResponseType[number];
  availabilityTierId: number;
  onBulkToggle: (ids: number[], active: boolean) => void;
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  const productStatuses = parent.Products.map((product) => {
    const isActive = product.ProductAvailabilityTiers.some(
      (at) => at.AvailabilityTierId === availabilityTierId && at.active,
    );
    return {
      id: product.id,
      active: isActive,
    };
  });

  const allActive = productStatuses.every((p) => p.active);
  const allInactive = productStatuses.every((p) => !p.active);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = !allActive && !allInactive;
    }
  }, [allActive, allInactive]);

  const handleToggle = (checked: boolean) => {
    let affectedIds: number[];

    if (!checked) {
      affectedIds = productStatuses.filter((p) => p.active).map((p) => p.id); // Turn OFF
    } else {
      affectedIds = productStatuses.filter((p) => !p.active).map((p) => p.id); // Turn ON
    }

    onBulkToggle(affectedIds, !allActive);
  };

  return (
    <input
      ref={checkboxRef}
      type="checkbox"
      checked={allActive}
      onChange={(e) => handleToggle(e.target.checked)}
    />
  );
};
